/**
 * Animated streak number — UI Wave A.
 * Shows the streak count with a pulsing flame emoji + an SVG ring that
 * closes when today's goal is hit. Drop-in replacement for a plain number.
 *
 * Props:
 *   count: current streak in days
 *   todayCompleted: bool — has the student answered ≥1 question today?
 *   onMilestone: fired when count hits a milestone (caller can fire confetti)
 */
import { useEffect, useRef } from "react";
import { streakBurst } from "@/lib/confetti";

const MILESTONES = new Set([3, 7, 14, 30, 60, 100, 180, 365]);

export default function AnimatedStreak({ count = 0, todayCompleted = false, onMilestone }) {
  const lastCountRef = useRef(count);

  useEffect(() => {
    if (count > lastCountRef.current) {
      if (MILESTONES.has(count)) {
        try { streakBurst(); } catch { /* confetti is non-critical */ }
        onMilestone?.(count);
      }
    }
    lastCountRef.current = count;
  }, [count, onMilestone]);

  // SVG ring math — completed fraction = 1 if today done, else partial
  const R = 18;
  const C = 2 * Math.PI * R;
  const dashoffset = todayCompleted ? 0 : C * 0.4;

  return (
    <div className="relative inline-flex items-center gap-2" data-testid="animated-streak">
      <div className="relative w-12 h-12 grid place-items-center">
        <svg viewBox="0 0 44 44" className="absolute inset-0">
          <circle cx="22" cy="22" r={R} stroke="#fef3c7" strokeWidth="3" fill="none" />
          <circle
            cx="22"
            cy="22"
            r={R}
            stroke="url(#streakGrad)"
            strokeWidth="3"
            fill="none"
            strokeDasharray={C}
            strokeDashoffset={dashoffset}
            strokeLinecap="round"
            transform="rotate(-90 22 22)"
            style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}
            data-testid="streak-ring"
          />
          <defs>
            <linearGradient id="streakGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f59e0b" />
              <stop offset="50%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#e11d48" />
            </linearGradient>
          </defs>
        </svg>
        <span
          className={`text-xl leading-none ${count > 0 ? "pm-flame-pulse" : "opacity-40"}`}
          aria-hidden
        >
          🔥
        </span>
      </div>
      <div className="leading-tight">
        <div className="font-display font-black text-xl tabular-nums bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 bg-clip-text text-transparent" data-testid="streak-count">
          {count}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {count === 1 ? "day" : "days"}
        </div>
      </div>
    </div>
  );
}
